// packages/ui/src/components/AppIcon.tsx
import { type ComponentType, type SVGAttributes } from 'react'

import { cn } from '../utils'

/** Tailles autorisées design system NeuroForge */
type IconSize = 16 | 20 | 24 | 28

const SIZE_MAP: Record<IconSize, string> = {
  16: 'w-4 h-4', // inline
  20: 'w-5 h-5', // UI dense (default)
  24: 'w-6 h-6', // nav / actions
  28: 'w-8 h-8', // nav / actions
}

export interface AppIconProps {
  /** Composant Tabler (ex: IconSearch) */
  icon: ComponentType<
    SVGAttributes<SVGSVGElement> & { size?: number | string; stroke?: number | string }
  >
  /** 16 = inline, 20 = UI dense, 24 = nav/actions */
  size?: IconSize
  /** Stroke width — default 1.75 (outline style) */
  stroke?: number
  /** Tailwind classes (couleur, spacing, etc.) */
  className?: string
  /** true → aria-hidden="true" (icône décorative) */
  'aria-hidden'?: boolean
  /** Accessible label pour boutons icon-only */
  'aria-label'?: string
  /** onClick handler */
  onClick?: () => void
  /** SVG fill — useful for star/heart toggle states (e.g. 'currentColor' | 'none') */
  fill?: string
}

/**
 * Wrapper icône standardisé — utilise @tabler/icons-react en outline.
 * Couleurs via classes Tailwind (text-(--muted), etc.), jamais hardcodées.
 *
 * @example
 * // Icône décorative dans un input
 * <AppIcon icon={IconSearch} size={16} className="text-(--muted)" aria-hidden />
 *
 * // Bouton icon-only accessible
 * <button aria-label="Rechercher">
 *   <AppIcon icon={IconSearch} size={24} />
 * </button>
 *
 * // Nav item avec icône
 * <AppIcon icon={IconHome} size={20} className="text-(--accent)" />
 */
export function AppIcon({
  icon: Icon,
  size = 20,
  stroke = 1.75,
  className,
  ...rest
}: AppIconProps) {
  return (
    <Icon size={size} stroke={String(stroke)} className={cn(SIZE_MAP[size], className)} {...rest} />
  )
}
